import * as moment from "moment-timezone";
import * as React from "react";
import styled, { css } from "styled-components";
import * as yup from "yup";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import * as _ from "lodash";
import { useMappedState } from "redux-react-hook";

import { Button } from "src/library/components/Button";
import { Icon } from "src/library/components/Icon";
import {
  FormRow,
  Input,
  Label,
  Radio
} from "src/library/components/Forms";
import { Icons } from "src/library/components/Icon/types";
import { DateInput } from "src/core/shared/components/Date/DateInput";
import { CutoffTimeSelection } from "src/core/shared/components/CutoffTimeSelection";
import { hourMinuteTimeSplit } from "src/library/utils/time-helpers/time-helpers";
import useTimezone from "src/core/shared/hooks/time-helpers/useTimeZone";
import {
  CustomerBranchFilters,
  CustomerBranchFiltersModal
} from "src/library/components/CustomerBranchFilter";
import { CustomerBranchFilterAction } from "src/library/components/CustomerBranchFilter/CustomerBranchFilters";
import {
  CustomerBranchFilterDefault,
  CustomerBranchFilterType
} from "src/core/products/api/products.api";
import { ICustomer } from "src/core/customers/api/customers.api";
import useTerminology from "src/core/shared/hooks/useTerminology";
import useCache from "src/core/shared/hooks/useCache";
import { GLOBAL_SETTINGS_CACHE_KEY } from "src/core/shared/api/globalSettings.api";
import { IStore } from "src/core/shared/store";
import { ErrorNotice } from "src/core/shared/components/ErrorNotice";

import {
  useLeadTimeOverrideApi,
  ILeadTimeOverride,
  CreateLeadTimeOverrideFilterParams
} from "../api/lead-time-override.api";

interface ILeadTimeOverrideFormProps {
  initialLeadTimeOverride: ILeadTimeOverride | null;
}

const LeadTimeOverrideForm: React.ReactFCWithChildren<ILeadTimeOverrideFormProps> = props => {
  const { initialLeadTimeOverride } = props;
  const terminology = useTerminology();
  const history = useHistory();
  const [ createLeadTimeOverrideRes, createLeadTimeOverrideReq ] = useLeadTimeOverrideApi("CREATE_LEAD_TIME_OVERRIDE");
  const [ updateLeadTimeOverrideRes, updateLeadTimeOverrideReq ] = useLeadTimeOverrideApi("UPDATE_LEAD_TIME_OVERRIDE");
  const timezone = useTimezone();
  const [ showFiltersModal, setShowFiltersModal ] = React.useState(false);
  const [ selectedCustomer, setSelectedCustomer ] = React.useState<ICustomer | null>(null);
  const existingLeadTimeOverrides = useMappedState(({ global }: IStore) => global.settings?.leadTimeOverrides || []);
  const { removeItemByKey } = useCache();

  const [ validation, setValidation ] = React.useState<{ error: boolean; message: string }>({
    error: false,
    message: ""
  });

  const [ form, setForm ] = React.useState<Partial<ILeadTimeOverride>>(initialLeadTimeOverride ? {
    ...initialLeadTimeOverride,
    deliveryDate: initialLeadTimeOverride.deliveryDate,
    cutoffDateTime: moment.tz(initialLeadTimeOverride.cutoffDateTime, timezone).format("YYYY-MM-DD HH:mm"),
    type: initialLeadTimeOverride.type,
    name: initialLeadTimeOverride.name
  } : {});

  // handles the form events to set the values
  const handleFormChange = React.useCallback(
    (name: keyof ILeadTimeOverride, value: any) => {
      setValidation({
        error: false,
        message: ""
      });

      if (name === "deliveryDate") {
        form.cutoffDateTime = undefined;
      }

      setForm({
        ...form,
        [ name ]: value
      });
    },
    [ form ]
  );

  // performs the relevant api request based on id
  const saveLeadTimeOverride = React.useCallback(() => {
    if (form.id) {
      updateLeadTimeOverrideReq({
        leadTimeOverrideId: form.id,
        leadTimeOverrideUpdate: {
          ...form,
          cutoffDateTime: moment.utc(form.cutoffDateTime).toISOString()
        }
      });
    } else {
      createLeadTimeOverrideReq({
        ...form,
        cutoffDateTime: moment.utc(form.cutoffDateTime).toISOString()
      } as ILeadTimeOverride);
    }
  }, [
    form,
    createLeadTimeOverrideReq,
    updateLeadTimeOverrideReq
  ]);

  // handles the submit button event
  const handleSubmit = React.useCallback(() => {
    leadTimeOverrideSchema
      .validate(form)
      .then(() => {
        saveLeadTimeOverride();
      })
      .catch(err => {
        setValidation({
          error: true,
          message: err.errors
        });
      });
  }, [ form, saveLeadTimeOverride ]);

  // Remove a filter from the array
  const removeFilter = React.useCallback((filter: CreateLeadTimeOverrideFilterParams) => {
    const newFilters = _.remove([ ...form.filters || [] ], f => {
      if (filter.customerId) {
        return f.customerId !== filter.customerId;
      }

      if (filter.branchId) {
        return (f.branchId !== filter.branchId);
      }

      return true;
    });

    handleFormChange("filters", [ ...newFilters ]);
  }, [ form.filters, handleFormChange ]);

  React.useEffect(() => {
    if (createLeadTimeOverrideRes.data || updateLeadTimeOverrideRes.data) {
      removeItemByKey(GLOBAL_SETTINGS_CACHE_KEY);
      history.push("/platform/lead-time-overrides");
    }

    if (createLeadTimeOverrideRes.error || updateLeadTimeOverrideRes.error) {
      toast.error((createLeadTimeOverrideRes.error ? createLeadTimeOverrideRes.error.message : null) || (updateLeadTimeOverrideRes.error ? updateLeadTimeOverrideRes.error.message : "Error"));
    }
  }, [
    createLeadTimeOverrideRes,
    history,
    updateLeadTimeOverrideRes,
    removeItemByKey
  ]);

  const filterType = React.useMemo(() => {
    if (form.type === CustomerBranchFilterDefault.AllowAll) {
      if (selectedCustomer) {
        return CustomerBranchFilterType.Include;
      } else {
        return CustomerBranchFilterType.Exclude;
      }
    } else {
      if (selectedCustomer) {
        return CustomerBranchFilterType.Exclude;
      } else {
        return CustomerBranchFilterType.Include;
      }
    }
  }, [ form.type, selectedCustomer ]);

  const allowAllForDayExists = React.useMemo(() => {
    return _.find(existingLeadTimeOverrides, lto => moment.utc(form.deliveryDate).isSame(moment.utc(lto.deliveryDate), "day") && lto.type === CustomerBranchFilterDefault.AllowAll);
  }, [ existingLeadTimeOverrides, form.deliveryDate ]);

  React.useEffect(() => {
    if (form.type !== CustomerBranchFilterDefault.AllowNone && allowAllForDayExists) {
      handleFormChange("type", CustomerBranchFilterDefault.AllowNone);
    }
  }, [
    allowAllForDayExists,
    form.type,
    handleFormChange
  ]);

  return (
    <FormContainer>
      {validation.error && (
        <ValidationContainer>
          <Icon
            $fillColor="error"
            icon={Icons.ERRORCIRCLE}
          />

          {validation.message}
        </ValidationContainer>
      )}

      <FormRow style={{ position: "relative" }}>
        <FormRow>
          <Input
            as="input"
            handleChange={e => handleFormChange("name", e.target.value)}
            name="name"
            label="Name"
            inputType="text"
            defaultValue={initialLeadTimeOverride?.name ? initialLeadTimeOverride.name : ""}
          />
        </FormRow>

      </FormRow>

      <FormRow style={{ position: "relative" }}>
        <Label
          label="Select delivery date to override"
          for="date-range"
        />

        <DayRangePickerWrapper>
          <DateInput
            label="Delivery Date"
            date={form.deliveryDate ? moment.utc(form.deliveryDate).toDate() : undefined}
            minimumDate={new Date()}
            setDate={date => {
              handleFormChange("deliveryDate", date);
            }}
          />
        </DayRangePickerWrapper>

      </FormRow>

      <FormRow style={{ position: "relative" }}>
        <Label
          label="Select a cutoff date and time for the delivery date selected"
          for="date-range"
        />

        <div className="date-time-row">
          <DayRangePickerWrapper>
            <DateInput
              label="Cutoff date"
              date={form.cutoffDateTime ? moment.utc(form.cutoffDateTime).toDate() : undefined}
              maximumDate={form.deliveryDate ? moment.utc(form.deliveryDate).toDate() : undefined}
              minimumDate={form.deliveryDate ? moment.utc(form.deliveryDate).subtract(100, "days").toDate() : undefined}
              initialFocusDate={moment.utc(form.deliveryDate).toDate()}
              setDate={date => {
                if (form.cutoffDateTime) {
                  const existingTimeHours = moment.utc(form.cutoffDateTime).hours();
                  const existingTimeMinutes = moment.utc(form.cutoffDateTime).minutes();
                  const newDate = moment.utc(date).set("hours", existingTimeHours).set("minutes", existingTimeMinutes).set("seconds", 0);

                  handleFormChange("cutoffDateTime", newDate.toDate());
                } else {
                  handleFormChange("cutoffDateTime", date);
                }
              }}
            />
          </DayRangePickerWrapper>

          {form.cutoffDateTime && (

            <CutoffTimeSelection
              setTime={time => {
                if (form.cutoffDateTime) {
                  const { hours, minutes } = hourMinuteTimeSplit(time);
                  const newCutoffDateTime = moment.utc(form.cutoffDateTime).set("hours", hours).set("minutes", minutes).set("seconds", 0);

                  handleFormChange("cutoffDateTime", newCutoffDateTime.toDate());
                }
              }}
              value={moment.utc(form.cutoffDateTime).format("HH:mm")}
              name="cutoff-time"
              timezone={timezone}
            />

          )}
        </div>
      </FormRow>

      {form.deliveryDate && (
        <FormRow>

          {allowAllForDayExists ? (
            <ErrorNotice
              message={`There is already a lead time override that applies to all customers for this delivery date. 
                        You can create more then one lead time override per delivery date but you must select which ${terminology?.customer.plural.text}/${terminology?.drop.plural.text} it applies to below. 
                        This override will take priority over the globally applied lead time override.
                        Alternatively you can change the existing lead time override for this delivery date`}
              type="warn"
            />
          ) : (
            <>
              <Label
                label="By default, should this lead time override be set for all customers or just to a few selected customers?"
                for="type"
              />

              <Radio
                disabled={!!form.id}
                options={[
                  {
                    text: `Apply this cutoff to all ${terminology?.customer.plural.text}`,
                    value: CustomerBranchFilterDefault.AllowAll,
                    checked: form.type === CustomerBranchFilterDefault.AllowAll
                  },
                  {
                    text: `Only apply this cutoff for selected ${terminology?.customer.plural.text} and ${terminology?.drop.plural.text}`,
                    value: CustomerBranchFilterDefault.AllowNone,
                    checked: form.type === CustomerBranchFilterDefault.AllowNone
                  }
                ]}
                handleChange={e => {
                  handleFormChange("type", e.target.value);
                }}
                name="type"
                label=""
                value={form.type}
              />
            </>
          )}
        </FormRow>
      )}

      <CustomerBranchFiltersModal
        existingFilters={form.filters || []}
        isOpen={showFiltersModal}
        onDismiss={() => {
          setShowFiltersModal(false);
          setSelectedCustomer(null);
        }}
        handleSave={data => handleFormChange("filters", form.filters ? [ ...form.filters, ...data ] : data)}
        filterType={filterType}
        customer={selectedCustomer}
      />

      {form.type && (
        <CustomerBranchFilters
          form={form}
          setSelectedCustomer={setSelectedCustomer}
          setShowFiltersModal={setShowFiltersModal}
          removeFilter={removeFilter}
          action={CustomerBranchFilterAction.LeadTimeOverride}
        />
      )}

      <SubmitContainer>
        <Button
          loading={createLeadTimeOverrideRes.pending || updateLeadTimeOverrideRes.pending}
          $buttonType="primary"
          onClick={() => handleSubmit()}
        >
          {initialLeadTimeOverride ? "Update override" : "Create override"}
        </Button>
      </SubmitContainer>
    </FormContainer>
  );
};

const ValidationContainer = styled.div`
  ${({ theme }) => css`
    padding: ${theme.spacing.padding.md} 0;
    color: ${theme.colors.error};
    display: flex;
    align-items: center;

    svg {
      margin-right: ${theme.spacing.padding.sm};
    }
  `}
`;

const DayRangePickerWrapper = styled.div`
  .DayPicker {
    width: 225px;
  }
`;

const SubmitContainer = styled.div`
  ${({ theme }) => css`
    padding-top: ${theme.spacing.padding.md};
    > * {
      margin: 0 ${theme.spacing.padding.xs};
    }

    > button:first-child {
      margin: 0 -${theme.spacing.padding.xs};
    }
  `}
`;

const FormLine = styled.div`
  ${({ theme }) => css`
    padding: ${theme.spacing.padding.md} 0;
  `}
`;

const FormContainer = styled.div`
  &.suspended ${FormLine} {
    opacity: 0.5;
    pointer-events: none;
  }

  .date-time-row {
    display: flex;
  }
`;

export const leadTimeOverrideSchema = yup.object().shape({
  deliveryDate: yup
    .date()
    .label("Delivery date")
    .required(),
  cutoffDateTime: yup
    .date()
    .label("Delivery date")
    .required()
});

export default LeadTimeOverrideForm;
